import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

const PATH = '/v1/me/profile'

/**
 * The signed-in person's own record. `fullName` is null until the person gives a name: a login
 * made by magic link has only its email.
 */
export const profileSchema = z.object({
  email: z.string(),
  fullName: z.string().nullable(),
  phone: z.string().nullable(),
  nationality: z.string().nullable(),
})

export type Profile = z.infer<typeof profileSchema>

/** What the form edits: every input as the control holds it, `''` for nothing. */
export type ProfileDraft = { fullName: string; phone: string; nationality: string }

export type ProfileBody = { fullName: string; phone: string | null; nationality: string | null }

export function fetchProfile(signal?: AbortSignal): Promise<Profile> {
  return api.get(PATH, { schema: profileSchema, ...(signal ? { signal } : {}) })
}

/** The whole record, every time: the endpoint is a PUT, and `null` clears a field. */
export function updateProfile(body: ProfileBody): Promise<Profile> {
  return api.put(PATH, { schema: profileSchema, body })
}

export function draftOf(profile: Profile): ProfileDraft {
  return {
    fullName: profile.fullName ?? '',
    phone: profile.phone ?? '',
    nationality: profile.nationality ?? '',
  }
}

const orNull = (value: string) => (value.trim() === '' ? null : value.trim())

export function bodyOf(draft: ProfileDraft): ProfileBody {
  return {
    fullName: draft.fullName.trim(),
    phone: orNull(draft.phone),
    nationality: orNull(draft.nationality),
  }
}

/** The one field the server requires. The server checks it too. */
export function nameIssue(draft: ProfileDraft): string | undefined {
  return draft.fullName.trim() === '' ? 'Enter your full name.' : undefined
}
